import { BrandLoader } from "@/components/brand/BrandLoader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Invite, PermLevel, RoleDef } from "@/lib/api/members";
import { memberService } from "@/lib/api/members";
import { canWrite } from "@/lib/permissions";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Copy, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const RESOURCES = [
  "properties",
  "rooms",
  "reservations",
  "guests",
  "guides",
  "chat",
  "reports",
  "channels",
  "members",
] as const;

const RESOURCE_LABELS: Record<string, string> = {
  properties: "숙소 설정",
  rooms: "객실",
  reservations: "예약",
  guests: "게스트",
  guides: "가이드",
  chat: "컨시어지",
  reports: "리포트 · 계약",
  channels: "채널",
  members: "멤버 · 권한",
};
const ROLE_LABELS: Record<string, string> = { owner: "오너", manager: "매니저", viewer: "뷰어" };

const roleLabel = (role: RoleDef) => ROLE_LABELS[role.name] ?? role.name;
const roleValue = (role: RoleDef) => (role.builtin ? role.name : role.id);

/** Skip the local error toast on 403 — the axios interceptor already shows a global one. */
function onMutationError(e: unknown, message: string) {
  if (isAxiosError(e) && e.response?.status === 403) return;
  toast.error(message);
}

const emptyPerms = (): Record<string, PermLevel> =>
  Object.fromEntries(RESOURCES.map((r) => [r, "none" as PermLevel]));

// ─── Role edit dialog ───────────────────────────────────────────────────────

interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: RoleDef | null;
  isPending: boolean;
  onSubmit: (values: { name: string; permissions: Record<string, PermLevel> }) => void;
}

function RoleFormDialog({ open, onOpenChange, role, isPending, onSubmit }: RoleFormDialogProps) {
  const [name, setName] = useState("");
  const [perms, setPerms] = useState<Record<string, PermLevel>>(emptyPerms());

  useEffect(() => {
    if (open) {
      setName(role?.name ?? "");
      setPerms(role ? { ...emptyPerms(), ...role.permissions } : emptyPerms());
    }
  }, [open, role]);

  const readOnly = Boolean(role?.builtin);

  const submit = () => {
    if (!name.trim()) {
      toast.error("역할 이름을 입력하세요.");
      return;
    }
    onSubmit({ name: name.trim(), permissions: perms });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{role ? "역할 수정" : "새 역할"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="role-name">역할 이름</Label>
            <Input
              id="role-name"
              value={name}
              disabled={readOnly}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            {RESOURCES.map((resource) => (
              <div key={resource} className="flex items-center justify-between gap-3">
                <span className="text-sm">{RESOURCE_LABELS[resource]}</span>
                <div className="flex gap-3">
                  {(["none", "read", "write"] as const).map((level) => (
                    <label key={level} className="flex items-center gap-1 text-xs">
                      <input
                        type="radio"
                        name={`perm-${resource}`}
                        aria-label={`${RESOURCE_LABELS[resource]}-${level}`}
                        disabled={readOnly}
                        checked={perms[resource] === level}
                        onChange={() => setPerms((p) => ({ ...p, [resource]: level }))}
                      />
                      {level}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            닫기
          </Button>
          {readOnly ? null : (
            <Button type="button" disabled={isPending} onClick={submit}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              저장
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function MembersPage() {
  const queryClient = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("");
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleDef | null>(null);

  const { data: perms } = useQuery({
    queryKey: ["my-permissions"],
    queryFn: memberService.myPermissions,
  });
  const writable = canWrite(perms?.permissions ?? {}, "members");

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["members"],
    queryFn: memberService.listMembers,
  });
  const { data: invites } = useQuery({
    queryKey: ["invites"],
    queryFn: memberService.listInvites,
  });
  const { data: roles } = useQuery({
    queryKey: ["roles"],
    queryFn: memberService.listRoles,
  });

  const assignableRoles = (roles ?? []).filter((r) => !(r.builtin && r.name === "owner"));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["members"] });
    queryClient.invalidateQueries({ queryKey: ["invites"] });
    queryClient.invalidateQueries({ queryKey: ["roles"] });
  };

  const updateMemberMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      memberService.updateMember(id, role),
    onSuccess: () => {
      invalidate();
      toast.success("역할이 변경되었습니다.");
    },
    onError: (e) => onMutationError(e, "역할 변경에 실패했습니다."),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (id: string) => memberService.removeMember(id),
    onSuccess: () => {
      invalidate();
      toast.success("멤버를 제거했습니다.");
    },
    onError: (e) => onMutationError(e, "멤버 제거에 실패했습니다."),
  });

  const createInviteMutation = useMutation({
    mutationFn: memberService.createInvite,
    onSuccess: (invite: Invite) => {
      setCreatedLink(invite.link);
      setInviteEmail("");
      invalidate();
      toast.success("초대를 생성했습니다.");
    },
    onError: (e) => onMutationError(e, "초대 생성에 실패했습니다."),
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (id: string) => memberService.revokeInvite(id),
    onSuccess: () => {
      invalidate();
      toast.success("초대를 취소했습니다.");
    },
    onError: (e) => onMutationError(e, "초대 취소에 실패했습니다."),
  });

  const createRoleMutation = useMutation({
    mutationFn: memberService.createRole,
    onSuccess: () => {
      invalidate();
      setRoleDialogOpen(false);
      toast.success("역할을 생성했습니다.");
    },
    onError: (e) => onMutationError(e, "역할 생성에 실패했습니다."),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: { id: string; body: { name?: string; permissions?: Record<string, PermLevel> } }) =>
      memberService.updateRole(id, body),
    onSuccess: () => {
      invalidate();
      setRoleDialogOpen(false);
      toast.success("역할을 수정했습니다.");
    },
    onError: (e) => onMutationError(e, "역할 수정에 실패했습니다."),
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => memberService.deleteRole(id),
    onSuccess: () => {
      invalidate();
      toast.success("역할을 삭제했습니다.");
    },
    onError: (e) => {
      if (isAxiosError(e) && e.response?.status === 409) {
        toast.error("이 역할을 사용 중인 멤버가 있어 삭제할 수 없습니다.");
        return;
      }
      onMutationError(e, "역할 삭제에 실패했습니다.");
    },
  });

  const submitInvite = () => {
    if (!inviteRole) {
      toast.error("역할을 선택하세요.");
      return;
    }
    createInviteMutation.mutate({ email: inviteEmail.trim() || null, role: inviteRole });
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("초대 링크가 복사되었습니다.");
    } catch {
      toast.error("복사에 실패했습니다.");
    }
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleDialogOpen(true);
  };
  const openEditRole = (role: RoleDef) => {
    setEditingRole(role);
    setRoleDialogOpen(true);
  };
  const submitRole = (values: { name: string; permissions: Record<string, PermLevel> }) => {
    if (editingRole) {
      updateRoleMutation.mutate({ id: editingRole.id, body: values });
    } else {
      createRoleMutation.mutate(values);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">멤버 · 권한</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          숙소 관리자 계정을 초대하고 역할별 권한을 관리합니다.
        </p>
      </div>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">멤버</TabsTrigger>
          <TabsTrigger value="roles">역할</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="space-y-6">
          <Card>
            {membersLoading ? (
              <BrandLoader className="py-12" size={44} label="불러오는 중…" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>이름</TableHead>
                    <TableHead>이메일</TableHead>
                    <TableHead>역할</TableHead>
                    {writable ? <TableHead className="text-right">관리</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(members ?? []).map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="text-muted-foreground">{m.email}</TableCell>
                      <TableCell>
                        {m.is_owner ? (
                          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            오너
                          </span>
                        ) : writable ? (
                          <Select
                            value={m.role}
                            onValueChange={(role) =>
                              updateMemberMutation.mutate({ id: m.id, role })
                            }
                          >
                            <SelectTrigger className="h-8 w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {assignableRoles.map((r) => (
                                <SelectItem key={r.id} value={roleValue(r)}>
                                  {roleLabel(r)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          (ROLE_LABELS[m.role] ?? m.role)
                        )}
                      </TableCell>
                      {writable ? (
                        <TableCell className="text-right">
                          {m.is_owner ? null : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!window.confirm(`${m.name}님을 제거할까요?`)) return;
                                removeMemberMutation.mutate(m.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" /> 제거
                            </Button>
                          )}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {writable ? (
            <Card className="space-y-4 p-4">
              <h2 className="font-medium">멤버 초대</h2>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="invite-email">이메일 (선택)</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-56"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="invite-role">역할</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger id="invite-role" className="w-40">
                      <SelectValue placeholder="선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignableRoles.map((r) => (
                        <SelectItem key={r.id} value={roleValue(r)}>
                          {roleLabel(r)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button disabled={createInviteMutation.isPending} onClick={submitInvite}>
                  {createInviteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  초대 생성
                </Button>
              </div>
              {createdLink ? (
                <div className="flex items-center gap-2 rounded-md border border-border/60 bg-secondary/40 p-3 text-sm">
                  <span className="flex-1 truncate">{createdLink}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => copyLink(createdLink)}
                  >
                    <Copy className="h-4 w-4" /> 복사
                  </Button>
                </div>
              ) : null}
            </Card>
          ) : null}

          <Card>
            <div className="border-b border-border/60 p-4">
              <h2 className="font-medium">대기 중인 초대</h2>
            </div>
            {invites && invites.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>이메일</TableHead>
                    <TableHead>역할</TableHead>
                    <TableHead>만료일</TableHead>
                    {writable ? <TableHead className="text-right">관리</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell>{invite.email ?? "이메일 미지정"}</TableCell>
                      <TableCell>
                        {ROLE_LABELS[invite.role] ??
                          roles?.find((r) => roleValue(r) === invite.role)?.name ??
                          invite.role}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(invite.expires_at).toLocaleDateString()}
                      </TableCell>
                      {writable ? (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => revokeInviteMutation.mutate(invite.id)}
                          >
                            취소
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="p-4 text-sm text-muted-foreground">대기 중인 초대가 없습니다.</p>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              내장 역할(오너·매니저·뷰어)은 권한을 확인만 할 수 있습니다.
            </p>
            {writable ? (
              <Button onClick={openCreateRole}>
                <Plus className="h-4 w-4" /> 새 역할
              </Button>
            ) : null}
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>역할</TableHead>
                  <TableHead>구분</TableHead>
                  {writable ? <TableHead className="text-right">관리</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(roles ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{roleLabel(r)}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          r.builtin
                            ? "bg-muted text-muted-foreground"
                            : "bg-primary/10 text-primary"
                        }`}
                      >
                        {r.builtin ? "내장" : "커스텀"}
                      </span>
                    </TableCell>
                    {writable ? (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={r.builtin ? "권한 보기" : "수정"}
                            onClick={() => openEditRole(r)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {r.builtin ? null : (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="삭제"
                              onClick={() => {
                                if (!window.confirm(`${r.name} 역할을 삭제할까요?`)) return;
                                deleteRoleMutation.mutate(r.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <RoleFormDialog
        open={roleDialogOpen}
        onOpenChange={setRoleDialogOpen}
        role={editingRole}
        isPending={createRoleMutation.isPending || updateRoleMutation.isPending}
        onSubmit={submitRole}
      />
    </div>
  );
}
